import { useState, useEffect } from 'react';
import api, { handleApiError } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  Download, Printer, Search, ChevronLeft,
  FileText, Calendar, RotateCcw,
} from 'lucide-react';

function SkeletonRow({ cols = 4 }) {
  return (
    <div className="flex gap-4 items-center py-3 px-6">
      {[...Array(cols)].map((_, i) => (
        <div key={i} className="skeleton h-4 flex-1" />
      ))}
    </div>
  );
}

function ReportRow({ label, amount, bold, indent, highlight, negative }) {
  return (
    <div className={`flex items-center justify-between py-2.5 px-6 ${bold ? 'font-semibold' : ''} ${highlight ? 'bg-primary-50 dark:bg-primary-900/20 border-y border-primary-200 dark:border-primary-700' : ''} ${indent ? 'pl-12' : 'pl-6'}`}>
      <span className={`text-sm ${bold ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>{label}</span>
      <span className={`text-sm ${bold ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'} ${negative ? 'text-danger-600 dark:text-danger-400' : ''}`}>
        {formatCurrency(amount)}
      </span>
    </div>
  );
}

function AccountDetailRow({ account }) {
  return (
    <div className="flex items-center justify-between py-2 px-12">
      <span className="text-sm text-gray-600 dark:text-gray-400">{account.code} - {account.name}</span>
      <span className="text-sm text-gray-700 dark:text-gray-300">{formatCurrency(account.amount)}</span>
    </div>
  );
}

export default function ProfitLossPage() {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [period, setPeriod] = useState(currentMonth);
  const [comparePeriod, setComparePeriod] = useState('');
  const [showCompare, setShowCompare] = useState(false);
  const [data, setData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRevenueDetail, setShowRevenueDetail] = useState(true);
  const [showCOGSDetail, setShowCOGSDetail] = useState(false);
  const [showOpexDetail, setShowOpexDetail] = useState(false);

  useEffect(() => {
    fetchData();
  }, [period]);

  useEffect(() => {
    if (showCompare && comparePeriod) {
      fetchCompareData();
    }
  }, [comparePeriod, showCompare]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/profit-loss', { params: { period } });
      setData(res.data.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompareData = async () => {
    try {
      const res = await api.get('/reports/profit-loss', { params: { period: comparePeriod } });
      setCompareData(res.data.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const toggleCompare = () => {
    if (showCompare) {
      setShowCompare(false);
      setCompareData(null);
      setComparePeriod('');
    } else {
      setShowCompare(true);
    }
  };

  const formatPeriodLabel = (p) => {
    if (!p) return '';
    const [y, m] = p.split('-');
    const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
    return `${months[parseInt(m) - 1]} ${y}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Laporan Laba Rugi</h1>
          <p className="page-subtitle">Laporan Pendapatan dan Beban (Income Statement)</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => toast.success('Export PDF - Coming Soon')} className="btn btn-outline btn-sm">
            <Download className="h-4 w-4 mr-1.5" /> PDF
          </button>
          <button onClick={() => toast.success('Export Excel - Coming Soon')} className="btn btn-outline btn-sm">
            <Download className="h-4 w-4 mr-1.5" /> Excel
          </button>
          <button onClick={() => window.print()} className="btn btn-outline btn-sm">
            <Printer className="h-4 w-4 mr-1.5" /> Cetak
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="label">Periode</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="month"
                  className="input pl-9"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                />
              </div>
            </div>
            <div>
              <button onClick={toggleCompare} className={`btn ${showCompare ? 'btn-primary' : 'btn-outline'} btn-sm mt-6`}>
              <RotateCcw className="h-4 w-4 mr-1.5" />
                {showCompare ? 'Bandingkan Aktif' : 'Bandingkan Periode'}
              </button>
            </div>
            {showCompare && (
              <div>
                <label className="label">Periode Pembanding</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="month"
                    className="input pl-9"
                    value={comparePeriod}
                    onChange={(e) => setComparePeriod(e.target.value)}
                  />
                </div>
              </div>
            )}
            <div className="flex justify-end mt-6">
              <button onClick={fetchData} className="btn btn-primary btn-sm">
                <Search className="h-4 w-4 mr-1.5" /> Muat Ulang
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Report Content */}
      {loading ? (
        <div className="card">
          <div className="card-header">
            <div className="skeleton h-5 w-48" />
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {[...Array(12)].map((_, i) => <SkeletonRow key={i} />)}
          </div>
        </div>
      ) : !data ? (
        <div className="card">
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Data laporan laba rugi tidak tersedia untuk periode ini.</p>
          </div>
        </div>
      ) : (
        <div className={`grid ${showCompare && compareData ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'} gap-6`}>
          {/* Main Report */}
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                <FileText className="h-5 w-5 mr-2 inline" />
                Laporan Laba Rugi
              </h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">{formatPeriodLabel(period)}</span>
            </div>

            {/* Revenue Section */}
            <div className="border-b border-gray-100 dark:border-gray-700">
              <button
                onClick={() => setShowRevenueDetail(!showRevenueDetail)}
                className="w-full flex items-center justify-between py-3 px-6 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <span className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">Pendapatan</span>
                {showRevenueDetail ? <ChevronLeft className="h-4 w-4 rotate-[-90deg] text-gray-400" /> : <ChevronLeft className="h-4 w-4 rotate-[90deg] text-gray-400" />}
              </button>
              {showRevenueDetail && data.revenue?.accounts?.map((acc) => (
                <AccountDetailRow key={acc.id} account={acc} />
              ))}
              <ReportRow label="Total Pendapatan" amount={data.revenue?.total} bold />
            </div>

            {/* COGS Section */}
            <div className="border-b border-gray-100 dark:border-gray-700">
              <button
                onClick={() => setShowCOGSDetail(!showCOGSDetail)}
                className="w-full flex items-center justify-between py-3 px-6 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <span className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">Harga Pokok Penjualan</span>
                {showCOGSDetail ? <ChevronLeft className="h-4 w-4 rotate-[-90deg] text-gray-400" /> : <ChevronLeft className="h-4 w-4 rotate-[90deg] text-gray-400" />}
              </button>
              {showCOGSDetail && data.cogs?.accounts?.map((acc) => (
                <AccountDetailRow key={acc.id} account={acc} />
              ))}
              <ReportRow label="Total HPP" amount={data.cogs?.total} bold />
            </div>

            {/* Gross Profit */}
            <ReportRow label="LABA KOTOR" amount={data.gross_profit} bold highlight />

            {/* Operating Expenses Section */}
            <div className="border-b border-gray-100 dark:border-gray-700">
              <button
                onClick={() => setShowOpexDetail(!showOpexDetail)}
                className="w-full flex items-center justify-between py-3 px-6 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <span className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">Beban Operasional</span>
                {showOpexDetail ? <ChevronLeft className="h-4 w-4 rotate-[-90deg] text-gray-400" /> : <ChevronLeft className="h-4 w-4 rotate-[90deg] text-gray-400" />}
              </button>
              {showOpexDetail && data.operating_expenses?.accounts?.map((acc) => (
                <AccountDetailRow key={acc.id} account={acc} />
              ))}
              <ReportRow label="Total Beban Operasional" amount={data.operating_expenses?.total} bold />
            </div>

            {/* Net Profit */}
            <ReportRow label="LABA BERSIH" amount={data.net_profit} bold highlight />
          </div>

          {/* Compare Report */}
          {showCompare && compareData && (
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  <FileText className="h-5 w-5 mr-2 inline" />
                  Perbandingan
                </h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">{formatPeriodLabel(comparePeriod)}</span>
              </div>

              <ReportRow label="Total Pendapatan" amount={compareData.revenue?.total} bold />
              <ReportRow label="Total HPP" amount={compareData.cogs?.total} bold />
              <ReportRow label="LABA KOTOR" amount={compareData.gross_profit} bold highlight />
              <ReportRow label="Total Beban Operasional" amount={compareData.operating_expenses?.total} bold />
              <ReportRow label="LABA BERSIH" amount={compareData.net_profit} bold highlight />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
